import { Button, Pill } from '@innovationdepartment/proxima-ui';
import type { ReactNode } from 'react';
import {
  CountActions,
  CountBlocker,
  CountNote,
  CountNumber,
  CountResult,
  CountRow
} from '../../components/form.styles';
import { MEMBER_COUNT_CAP, MIN_SEED_SIZE_WARNING } from '../../lib/filters';
import { formatNumber } from '../../lib/formatters';
import type { CountState } from '../../store/wizardTypes';

type CountBarProps = {
  count: CountState;
  stale: boolean;
  canCount: boolean;
  blockedReason: string | null;
  onCount: () => void;
  /** Sits after Preview Audience, e.g. the Show SQL toggle. */
  secondaryAction?: ReactNode;
  /** Labels of proposed-column rules that preview and create leave out. */
  notSent?: string[];
};

/** The member count for the current rules, below the rule rows. */
const CountBar = ({
  count,
  stale,
  canCount,
  blockedReason,
  onCount,
  secondaryAction,
  notSent = []
}: CountBarProps) => (
  <CountRow>
    <CountActions>
      <Button
        label={count.kind === 'counting' ? 'Previewing…' : 'Preview Audience'}
        size="small"
        variant="outlined"
        disabled={!canCount || count.kind === 'counting'}
        onClick={onCount}
      />
      {secondaryAction}
    </CountActions>
    {blockedReason ? (
      <CountBlocker role="alert">
        <Pill variant="warning" label={blockedReason} />
      </CountBlocker>
    ) : null}
    <CountDisplay count={count} stale={stale} />
    {notSent.length > 0 ? (
      <CountNote tone="muted" role="note" aria-label="Not sent">
        Not sent: {notSent.join(', ')}
      </CountNote>
    ) : null}
  </CountRow>
);

const CountDisplay = ({ count, stale }: { count: CountState; stale: boolean }) => {
  switch (count.kind) {
    case 'idle':
      return null;
    case 'counting':
      return <CountNote>Counting in Snowflake…</CountNote>;
    case 'error':
      return <CountNote tone="error">{count.message}</CountNote>;
    default: {
      const atCap = count.memberCount >= MEMBER_COUNT_CAP;
      return (
        <CountResult>
          <CountNumber stale={stale}>
            {atCap ? 'At least ' : ''}
            {formatNumber(count.memberCount)} members
          </CountNumber>
          {stale ? <CountNote>Rules changed since this count.</CountNote> : null}
          {!stale && atCap ? (
            <CountNote>At the {formatNumber(MEMBER_COUNT_CAP)} pool cap.</CountNote>
          ) : null}
          {!stale && count.memberCount < MIN_SEED_SIZE_WARNING ? (
            <CountNote tone="warning">Small audiences may fail Meta's minimum.</CountNote>
          ) : null}
        </CountResult>
      );
    }
  }
};

export default CountBar;
